import React, { useEffect, useState } from "react";
import { Card, Form, Input, Button, Typography, Spin, Result } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  validateExpertInviteToken,
  acceptExpertInvite,
} from "PlatForms/Dashboards/CORIQPROAdminDashboard/Services/ExpertInviteService";

const { Title, Text } = Typography;

const REASON_MESSAGES = {
  invalid_token: "This invitation link is not valid.",
  not_found: "We could not find this invitation.",
  already_used: "This invitation has already been accepted. Please log in.",
  revoked: "This invitation has been revoked.",
  expired: "This invitation has expired. Please ask for a new one.",
  unavailable: "This invitation is not available right now.",
};

const AcceptExpertInvite = () => {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null); // { isValid, email, firstName, lastName, reasonCode, message }
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setInvite({ isValid: false, reasonCode: "invalid_token" });
        setChecking(false);
        return;
      }
      try {
        const data = await validateExpertInviteToken(token);
        setInvite(data);
      } catch (error) {
        setInvite({
          isValid: false,
          reasonCode: error?.response?.data?.reasonCode || "unavailable",
        });
      } finally {
        setChecking(false);
      }
    };
    run();
  }, [token]);

  const handleAccept = async (values) => {
    try {
      setSubmitting(true);
      await acceptExpertInvite({ token, password: values.password });
      toast.success("Your expert account is ready. Please log in.");
      navigate("/login");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not accept the invitation.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (checking) {
      return (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Checking your invitation...</Text>
          </div>
        </div>
      );
    }

    if (!invite?.isValid) {
      const msg =
        invite?.message || REASON_MESSAGES[invite?.reasonCode] || "This invitation is not valid.";
      return (
        <Result
          status="warning"
          title="Invitation unavailable"
          subTitle={msg}
          extra={
            <Button type="primary" onClick={() => navigate("/login")} style={btnStyle}>
              Go to Login
            </Button>
          }
        />
      );
    }

    return (
      <>
        <div style={{ marginBottom: 16 }}>
          <Title level={3} style={{ color: "#9A6B0E", marginBottom: 4 }}>
            Welcome{invite.firstName ? `, ${invite.firstName}` : ""}
          </Title>
          <Text type="secondary">
            Set a password to activate your expert account
            {invite.email ? ` (${invite.email})` : ""}.
          </Text>
        </div>

        <Form name="accept-expert-invite" layout="vertical" onFinish={handleAccept}>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Please enter a password" },
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Confirm your password" />
          </Form.Item>

          <Form.Item>
            <motion.div whileTap={{ scale: 0.97 }}>
              <Button type="primary" htmlType="submit" loading={submitting} block style={btnStyle}>
                {submitting ? "Activating..." : "Accept & Create Account"}
              </Button>
            </motion.div>
          </Form.Item>
        </Form>

        <Text type="secondary">
          Already activated?{" "}
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate("/login")}>
            Log In
          </Button>
        </Text>
      </>
    );
  };

  return (
    <div style={styles.container}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: "100%", maxWidth: 480 }}
      >
        <Card style={styles.card} variant="borderless">
          {renderBody()}
        </Card>
      </motion.div>
    </div>
  );
};

const btnStyle = { backgroundColor: "#9A6B0E", borderColor: "#9A6B0E", color: "#fff" };

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    backgroundColor: "#f4f6f9",
  },
  card: {
    width: "100%",
    padding: "32px",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
  },
};

export default AcceptExpertInvite;
